import type { WordList } from '../types';

/**
 * English. Plain spellings only: disguises (`sh1t`, `f_u_c_k`, `fuuuck`, Cyrillic look-alikes)
 * are undone before matching, so they needn't be listed. Add common misspellings that change
 * the letters themselves (`phuck`).
 *
 * - `part`: anywhere, even inside another word. For words that are never innocent. Write a
 *   phrase with spaces (`kill yourself`): it matches with or without them, across words too.
 * - `word`: only as a whole word, where they hide inside innocent ones (`ass` in `class`).
 *
 * Changes are reviewed like code. The tests list innocent words that must keep passing.
 */
export const ENGLISH: WordList = {
  slur: {
    part: [
      'nigger',
      'nigga',
      'niglet',
      'faggot',
      'fagot',
      'tranny',
      'shemale',
      'retard',
      'wetback',
      'raghead',
      'towelhead',
      'darkie',
      'mongoloid',
      'spastic',
      'nazi',
      'hitler',
      'kkk',
      'white power',
      'sieg heil',
      'holohoax',
      'heil hitler',
      'gas the jews',
    ],
    word: [
      'fag',
      'fags',
      'kike',
      'kikes',
      'spic',
      'spics',
      'chink',
      'chinks',
      'gook',
      'gooks',
      'beaner',
      'beaners',
      'coon',
      'coons',
      'paki',
      'pakis',
      'dyke',
      'dykes',
      'homo',
      'homos',
      'lesbo',
      'spaz',
      'jap',
      'japs',
      'heil',
      'retards',
      'tard',
    ],
  },
  sexual: {
    part: [
      'porn',
      'pussy',
      'penis',
      'vagina',
      'dildo',
      'jizz',
      'orgasm',
      'blowjob',
      'handjob',
      'milf',
      'hentai',
      'horny',
      'titties',
      'cumshot',
      'creampie',
      'bukkake',
      'gangbang',
      'deepthroat',
      'rimjob',
      'masturbat',
      'erection',
      'onlyfans',
      'incest',
      'pedophil',
      'paedophil',
      'paedo',
      'molest',
      'rapist',
      'slut',
      'whore',
      'schlong',
      'ballsack',
      'scrotum',
      'testicle',
      'butthole',
      'buttplug',
      'bdsm',
      'boobies',
      'nympho',
      'sexting',
      'camgirl',
      'fellatio',
      'cunnilingus',
      'bestiality',
      'zoophil',
    ],
    word: [
      'sex',
      'sexy',
      'sexx',
      'nude',
      'nudes',
      'boob',
      'boobs',
      'tits',
      'titty',
      'cum',
      'cums',
      'cumming',
      'anal',
      'anus',
      'cock',
      'cocks',
      'dick',
      'dicks',
      'clit',
      'boner',
      'thot',
      'pedo',
      'pedos',
      'rape',
      'raped',
      'raping',
      'hooker',
      'stripper',
      'fetish',
      'nipple',
      'nipples',
    ],
  },
  profanity: {
    part: [
      'fuck',
      'phuck',
      'fck',
      'shit',
      'bitch',
      'biatch',
      'bastard',
      'cunt',
      'twat',
      'wank',
      'asshole',
      'arsehole',
      'dumbass',
      'jackass',
      'bollocks',
      'douche',
      'motherf',
      'dipshit',
      'shithead',
      'dickhead',
      'cocksucker',
    ],
    word: ['fuk', 'fuq', 'ass', 'asses', 'arse', 'piss', 'pissed', 'prick', 'pricks', 'stfu'],
  },
  violence: {
    part: [
      'school shooter',
      'school shooting',
      'mass shooter',
      'mass shooting',
      'beheading',
      // Not "I will kill you": that's ordinary trash talk in games.
      'ethnic cleansing',
    ],
    word: ['behead', 'lynching'],
  },
  // Telling someone else to hurt themselves.
  harassment: {
    part: [
      'kill yourself',
      'kill urself',
      'kill your self',
      'hang yourself',
      'slit your wrist',
      'cut yourself',
    ],
    word: ['kys'],
  },
  // Someone talking about hurting themselves: they may need help, not a wall.
  self_harm: {
    part: ['slit my wrist', 'suicide', 'self harm', 'want to die', 'kill myself'],
    word: ['kms'],
  },
  drugs: {
    part: ['cocaine', 'heroin', 'crackhead', 'methhead', 'fentanyl'],
    word: ['meth'],
  },
  impersonation: {
    part: ['mogzo', 'quarkfox', 'admin', 'moderator', 'official'],
    word: ['mod', 'mods', 'staff', 'support', 'sysop', 'helpdesk'],
  },
};

/**
 * Innocent words that contain a listed one. A match wholly inside one of these doesn't count
 * (the "Scunthorpe problem").
 */
export const ENGLISH_ALLOWED: readonly string[] = [
  'scunthorpe',
  'penistone',
  'shiitake',
  'shitake',
  'matsushita',
  'swank',
  'swanky',
  'therapist',
  'therapists',
  'heroine',
  'heroines',
  'nazir',
  'pussycat',
  'pussywillow',
  'snigger',
  'sniggers',
  'sniggering',
  'niggard',
  'niggardly',
  'vandyke',
  'badminton',
  'cockpit',
  'cocktail',
  'peacock',
  'hancock',
  'retardant',
];
